import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody, EmptyState, Spinner } from '@fluxora/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';


import { usePrefetchShelfForTdu } from '../api/plans.prefetch';
import { usePlansQuery } from '../api/plans.queries';
import { ContactFirstPanel } from '../components/ContactFirstPanel';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { MethodChooser } from '../components/MethodChooser';
import { PlanCard } from '../components/PlanCard';
import { ResolvedMeterPanel } from '../components/ResolvedMeterPanel';
import { Reveal } from '../components/Reveal';
import { SupplierChips } from '../components/SupplierChips';
import { DEFAULT_REFERENCE_USAGE } from '../schemas/shop.schema';
import { featuredPlans, uniqueSuppliers } from '../services/catalogue.service';
import { useFunnel } from '../state/FunnelProvider';

/**
 * The marketing landing page (E-SF08) — the storefront's front door at `/`.
 *
 * ── METER-FIRST REDESIGN (SHOP-2 slice 1) ─────────────────────────────────
 * The hero is now a METER-FIRST entry point: a `MethodChooser` (Shop by ESI
 * ID | Shop by ZIP) mounts above the "how it works" strip. Picking a ZIP +
 * an address resolves the meter through the existing BFF address-lookup —
 * no plan required at this stage. The resolved candidate lives in
 * `FunnelProvider` (mounted at the storefront layout root), so on resolve
 * the `MeterSummaryCard` slots in ABOVE the picker within the same hero card
 * and its "Shop plans" CTA sends the shopper to the shelf, which will read
 * the candidate from the funnel store on arrival.
 *
 * The marketing sections (how it works, suppliers, featured, closing CTA)
 * stay UNCHANGED below the chooser so the page keeps its selling job for a
 * shopper who is not yet ready to commit a ZIP.
 *
 * COMPLIANCE: every claim is factual (see the `landing.*` i18n notes). The
 * headline pricing language restates the §25.475 average-price rule the shelf
 * enforces; there are no fabricated ratings, no invented PUCT numbers, and the
 * supplier chips are real catalogue names, explicitly "not an endorsement".
 *
 * DATA STATES: the supplier + featured sections handle all five async states
 * against `usePlansQuery`; the meter-first hero handles them via the sub-flow
 * components. The static hero copy always renders so the page is never blank.
 */
export function LandingPage(): JSX.Element {
  const { t } = useTranslation();
  // The marketing sections genuinely want the WHOLE catalogue (supplier chips,
  // featured strip), so this stays unnarrowed.
  const plansQuery = usePlansQuery();
  const funnel = useFunnel();

  /* Whether step one is done, for THIS page view.
   *
   * Local state on purpose. It is a view concern, not funnel data: the contact
   * itself lives server-side in the BFF session, and the page has no business
   * holding a name, email or phone in the browser after it has been sent.
   *
   * A shopper who returns mid-funnel (an offer link, a reload after resolving a
   * meter) skips step one anyway, because `resolvedMeter` is already set — so
   * re-asking cannot happen just because this flag resets. */


  // …but the moment a meter resolves IN THE HERO ABOVE, start warming the shelf
  // the "Shop plans" CTA leads to. Narrowing made that shelf a separate cache
  // key, which would otherwise cost this shopper a cold list call the meter-first
  // shopper does not pay. Silent on failure, and a no-op once warm.
  usePrefetchShelfForTdu(funnel.meterDetail?.tduCode);

  const steps = [1, 2, 3] as const;
  // E-SF19 P1 — once a meter is resolved the strip may only feature THAT utility's
  // plans. QA saw an Oncor plan featured with CenterPoint confirmed on the same
  // screen, enrollable through to "Continue to approval".
  const featuredPlanCount = 3;
  const resolvedMeter = funnel.meterDetail;

  /* A FRESH page load starts the funnel from the top — details, then the meter
   * chooser — because nothing is restored from the server any more (every load
   * mints an empty session; see FunnelHydrationGate). This page therefore does
   * NOT clear the resolved meter on mount: within one visit, Back from the shelf
   * to here must show the meter the shopper already confirmed, not erase it —
   * which is what made Forward/Continue bounce back to this page. */

  return (
    <div className="flex flex-col">
      {/* Hero band — meter-first entry (the chooser + sub-flows OR the resolved card). */}
      <section id="meter-start" className="mx-auto w-full max-w-3xl scroll-mt-24 px-4 pt-6">
        {/* ⚠️ NO `overflow-hidden` ON THIS CARD — Azure #14553.
            It used to carry it, to keep the ambient washes below inside the
            rounded corners. But this card also contains the ZIP → ADDRESS
            picker, and `ListboxSelect` opens its panel as an ABSOLUTE child:
            clipping the card clipped the open dropdown. Measured at 1282x768 on
            ZIP 78401 — the panel ran 63px past the card, so 2 of 10 addresses
            were invisible AND unclickable, and the panel's own scrollbar was
            inside the hidden strip, which is why the list read as "not
            scrolling".
            The clip is now on the decoration layer below, which is the only
            thing that ever needed it. Do not move it back up here. */}
        <div className="relative rounded-lg bg-surface px-6 py-10 shadow-card sm:px-10">
          {/* Ambient washes in the LOGO's own hues — indigo top-left, coral
              bottom-right — so the card sits inside the gradient's world
              without the gradient itself shouting from the background.
              They sit OUTSIDE the card's bounds on purpose, so they need a
              clip — and it belongs here, around the decoration alone, rather
              than on the card that also holds an opening dropdown. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg"
          >
            <div className="absolute -left-16 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-2xl will-change-transform" />
            <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-accent/10 blur-2xl will-change-transform" />
          </div>
          <div className="anim-stagger relative flex flex-col items-center gap-4 text-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t('shop.landing.hero.eyebrow')}
            </span>
            {/* The one bold move: the display face at real scale, with the
                brand gradient living in the letterforms of the key phrase —
                the same gradient as the mark in the nav above it. */}
            <h1 className="font-display max-w-2xl text-3xl font-bold leading-[1.08] tracking-tight text-foreground sm:text-[2.6rem]">
              {t('shop.landing.hero.titleLead')}{' '}
              <span className="brand-gradient-text">{t('shop.landing.hero.titleBrand')}</span>
            </h1>
            <p className="max-w-xl text-sm text-foreground/70">
              {t('shop.landing.hero.subtitle')}
            </p>
          </div>

          {/*
            Meter-first entry, and the funnel's FIRST page. Two states on one route:
            the method chooser (Shop by ESI ID / ZIP), and the resolved-meter card once
            a candidate exists.

            ⚠️ THE BACK CONTROL APPEARS ONLY IN THE RESOLVED STATE. On the chooser there
            is genuinely nowhere to go — it is the entry point — and a back affordance
            that leaves the app is worse than none. From the resolved card, "back" means
            the chooser, so it clears the candidate and returns there: the same
            `clearResolved()` the card's own Start-over ran.

            The in-card Start-over is deliberately NOT passed here, so this page has ONE
            way back rather than two controls doing the same thing in different corners.
            `MeterSummaryCard` keeps the prop for its other callers.
          */}
          {/* THREE states now, in funnel order: contact → chooser → resolved.
              Contact comes first so an abandoned signup still leaves a lead an
              operator can follow up; before this, somebody who left here left
              nothing at all. It is a state, NOT a route — a new route would
              need its own gating and would break the offer-link entry, which
              lands mid-funnel and must never be sent back to step one. */}
          {/* Each state carries `.anim-step` on a KEYED wrapper: React remounts
              the wrapper when the step changes, so the entrance animation
              replays exactly once per transition — contact gliding out, the
              chooser gliding in — instead of only on first page load. */}
          <div className="relative mt-6 flex flex-col gap-3">
            {!funnel.leadContactCaptured ? (
              <div key="step-contact" className="anim-step">
                <ContactFirstPanel onCaptured={() => funnel.markLeadContactCaptured()} />
              </div>
            ) : resolvedMeter !== undefined ? (
              <div key="step-resolved" className="anim-step flex flex-col gap-3">
                <FunnelBackLink
                  onBack={() => funnel.clearResolved()}
                  label={t('shop.landing.backToStart')}
                />
                <ResolvedMeterPanel meterDetail={resolvedMeter} />
              </div>
            ) : (
              <div key="step-chooser" className="anim-step">
                <MethodChooser value={funnel.method} onChange={funnel.setMethod} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="mx-auto w-full max-w-6xl scroll-mt-24 px-4 py-14">
        <h2 className="font-display mb-8 text-center text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {t('shop.landing.how.heading')}
        </h2>
        {/* The three steps ARE a sequence, so the number chips carry the brand
            gradient — the numbering is information here, not decoration — and
            they enter one after another as the section scrolls into view,
            the same order a shopper walks them. */}
        <Reveal className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {steps.map((n) => (
            <Card key={n} variant="glass" padding="md" className="step-card hover-lift shadow-card">
              <CardBody className="flex flex-col items-center gap-3 p-0 text-center">
                <span className="step-chip flex h-10 w-10 items-center justify-center rounded-full bg-[image:var(--brand-gradient-tint)] text-sm font-bold text-primary shadow-pill">
                  {n}
                </span>
                <span className="font-display text-lg font-bold text-foreground">
                  {t(`shop.landing.how.step${n}.title`)}
                </span>
                <span className="max-w-[34ch] text-sm leading-relaxed text-foreground/70">
                  {t(`shop.landing.how.step${n}.body`)}
                </span>
              </CardBody>
            </Card>
          ))}
        </Reveal>
      </section>

      {/* Suppliers + Featured (data-derived) */}
      <section className="bg-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-12">
          {plansQuery.isPending ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" label={t('shop.landing.featured.loading')} />
            </div>
          ) : plansQuery.isError ? (
            <Alert variant="danger" title={t('shop.landing.featured.error')} role="alert">
              <Button variant="secondary" size="sm" onClick={() => void plansQuery.refetch()}>
                {t('shop.landing.featured.retry')}
              </Button>
            </Alert>
          ) : plansQuery.data.plans.length === 0 ? (
            <EmptyState title={t('shop.landing.featured.empty')} />
          ) : (
            <>
              <Reveal animation="anim-rise" className="flex flex-col items-center gap-4 text-center">
                <div className="flex flex-col gap-1">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">
                    {t('shop.landing.suppliers.heading')}
                  </h2>
                  <p className="text-sm text-foreground/60">{t('shop.landing.suppliers.note')}</p>
                </div>
                {/* Centred wrap: a ragged left-aligned chip cloud reads as a tag
                    dump; centred, each row balances itself left-and-right and
                    the strip reads as a roster. Real catalogue names — no
                    trademarked logos (unlicensed). */}
                {/* Full-width belt, not a capped column — the marquee needs
                    room to roll, and its masked edges are the framing. */}
                <div className="w-full">
                  <SupplierChips suppliers={uniqueSuppliers(plansQuery.data.plans)} marquee />
                </div>
              </Reveal>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center gap-1 text-center">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">
                    {t('shop.landing.featured.heading')}
                  </h2>
                  <p className="text-sm text-foreground/60">
                    {t('shop.landing.featured.subtitle')}
                  </p>
                </div>
                <Reveal className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {featuredPlans(
                    plansQuery.data.plans,
                    featuredPlanCount,
                    resolvedMeter?.tduCode,
                  ).map((plan) => (
                    /*
                      DELIBERATELY the §25.475 midpoint, not a shopper usage. This is
                      the PRE-METER marketing shelf: no meter is resolved yet, so
                      there is no personal kWh to price at, and every card must be
                      comparable on the same disclosed basis. The card's own label
                      says "at 1000 kWh/mo", so the basis is stated, not implied.
                    */
                    <PlanCard
                      key={plan.tariffId}
                      plan={plan}
                      monthlyKwh={DEFAULT_REFERENCE_USAGE}
                    />
                  ))}
                </Reveal>
                {/* Meter-first: there is no browse-all shelf — start by resolving a
                    meter (the chooser at the top of this page). A proper centred
                    control, not an underlined string hanging off the grid's
                    left corner. */}
                <div className="flex justify-center">
                  <Link to="/#meter-start">
                    <Button variant="secondary" shape="pill">
                      {t('shop.landing.featured.seeAll')}
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Closing CTA — the gradient's second appearance, but PASTEL: the hero
          wore it at full strength in the letterforms; the close wears a soft
          wash of the same stops, so the page still opens and closes on one
          palette without ending on a shout. One line, one button. */}
      <section className="mx-auto w-full max-w-6xl px-4 py-16">
        <Reveal
          animation="anim-rise"
          className="rounded-lg bg-[image:var(--brand-gradient-pastel)] px-6 py-14 text-center shadow-card sm:px-12"
        >
          <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t('shop.landing.closing.title')}
          </h2>
          <div className="mt-7 flex justify-center">
            {/* Meter-first: send the shopper to the method chooser at the top. */}
            <Link to="/#meter-start">
              <Button variant="primary" size="lg" shape="pill" className="cta-sheen">
                {t('shop.landing.closing.cta')}
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
